import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import OrderStatusPill from './ui/OrderStatusPill';
import {
    CATERING_QUOTES_LIST_PATH,
    fetchCateringQuoteById,
} from './cateringQuoteApi';
import EventLocationMapLink from './EventLocationMapLink';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Menu.css';
import './css/Inventory.css';

function formatMoney(value) {
    if (value == null || value === '') return '—';
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return String(value);
    return `₹${n.toFixed(2)}`;
}

function formatDateDisplay(raw) {
    if (!raw) return '—';
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [y, m, d] = s.slice(0, 10).split('-');
        return `${d}/${m}/${y}`;
    }
    return s;
}

const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
};

const DetailField = ({ label, value, wide = false, children }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
        <span className="restaurant-detail-label">{label}</span>
        {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
    </div>
);

const CateringQuoteDetail = () => {
    const { quoteId: quoteIdParam } = useParams();
    const navigate = useNavigate();
    const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
        useRestaurantEnabledServices();
    const quoteId = Number(quoteIdParam);

    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadQuote = useCallback(async () => {
        if (!Number.isFinite(quoteId) || quoteId <= 0) {
            setError('Invalid quote id.');
            setQuote(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const row = await fetchCateringQuoteById(quoteId);
            if (!row) {
                setError('Quote not found');
                setQuote(null);
                return;
            }
            setQuote(row);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to load quote'
            );
            setQuote(null);
        } finally {
            setLoading(false);
        }
    }, [quoteId]);

    useEffect(() => {
        loadQuote();
    }, [loadQuote]);

    const lineItems = quote?.lineItems ?? [];

    const lineItemsSubtotal = useMemo(() => {
        return lineItems.reduce((sum, li) => {
            const line = Number.parseFloat(li.price);
            return sum + (Number.isFinite(line) ? line : 0);
        }, 0);
    }, [lineItems]);

    const quoteTaxes = useMemo(() => {
        const toMoney = (v) => {
            const n = Number.parseFloat(v);
            return Number.isFinite(n) ? n : 0;
        };
        return {
            gst: toMoney(quote?.gst ?? quote?.GST),
            deliveryFee: toMoney(quote?.deliveryFee ?? quote?.Delivery_Fee),
            convienceFee: toMoney(quote?.convienceFee ?? quote?.Convience_Fee),
            platformFee: toMoney(quote?.platformFee ?? quote?.Platform_Fee),
        };
    }, [quote]);

    const handleSelectOrderChannel = (key) => {
        navigate(`/home?tab=orders&orderType=${key}`);
    };

    return (
        <RestaurantDashboardLayout
            activeTab="orders"
            orderChannel="catering"
            onSelectOrderChannel={handleSelectOrderChannel}
            enabledServiceTypes={enabledServiceTypes}
            servicesLoading={servicesLoading}
        >
            <div className="purchase-detail-page store-order-detail-page catering-quote-detail-page">
                <div className="restaurant-detail-panel">
                    {loading ? (
                        <div className="restaurant-detail-card">
                            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                                <div className="inventory-item-detail-header-row">
                                    <button
                                        type="button"
                                        className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                        onClick={() => navigate(CATERING_QUOTES_LIST_PATH)}
                                        aria-label="Back to quote requests"
                                    >
                                        <FaArrowLeft aria-hidden />
                                    </button>
                                    <h3 className="restaurant-detail-section-title">
                                        Catering Quote #{Number.isFinite(quoteId) ? quoteId : '—'}
                                    </h3>
                                </div>
                            </div>
                            <p className="order-details-loading">Loading quote…</p>
                        </div>
                    ) : null}

                    {!loading && error && !quote ? (
                        <div className="restaurant-detail-card">
                            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                                <div className="inventory-item-detail-header-row">
                                    <button
                                        type="button"
                                        className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                        onClick={() => navigate(CATERING_QUOTES_LIST_PATH)}
                                        aria-label="Back to quote requests"
                                    >
                                        <FaArrowLeft aria-hidden />
                                    </button>
                                    <h3 className="restaurant-detail-section-title">
                                        Catering Quote #{Number.isFinite(quoteId) ? quoteId : '—'}
                                    </h3>
                                </div>
                            </div>
                            <p className="restaurant-detail-message error">{error}</p>
                        </div>
                    ) : null}

                    {!loading && quote ? (
                        <>
                            <div className="restaurant-detail-card">
                                <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                                    <div className="inventory-item-detail-header-row">
                                        <button
                                            type="button"
                                            className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                            onClick={() => navigate(CATERING_QUOTES_LIST_PATH)}
                                            aria-label="Back to quote requests"
                                        >
                                            <FaArrowLeft aria-hidden />
                                        </button>
                                        <h3 className="restaurant-detail-section-title">
                                            Catering Quote #{quote.cateringQuoteId}
                                        </h3>
                                        <OrderStatusPill status={quote.quoteStatus ?? 'REQUESTED'} />
                                    </div>
                                </div>
                                <div className="restaurant-detail-grid">
                                    <DetailField label="Customer" value={quote.customerName || '—'} />
                                    <DetailField label="Phone" value={formatMobileDisplayOrFallback(quote.customerPhone, '—')} />
                                    <DetailField label="Customer ID" value={quote.customerId} />
                                    <DetailField label="Event Date" value={formatDateDisplay(quote.eventDate)} />
                                    <DetailField label="Event Time" value={quote.eventTime || '—'} />
                                    <DetailField label="Event Type" value={quote.eventType || '—'} />
                                    <DetailField label="Guests" value={quote.numberOfGuests ?? '—'} />
                                    <DetailField label="Package" value={quote.packageName || '—'} />
                                    <DetailField label="Price / Person" value={formatMoney(quote.pricePerPerson)} />
                                    <DetailField label="Delivery Type" value={quote.deliveryType || '—'} />
                                    <DetailField label="Estimated Total" value={formatMoney(quote.estimatedTotal)} />
                                    <DetailField label="Quoted Total" value={formatMoney(quote.quotedTotal)} />
                                    <DetailField label="Created On" value={quote.createdDate || '—'} />
                                    <DetailField label="Updated On" value={quote.updatedDate || '—'} />
                                    <div className="restaurant-detail-field restaurant-detail-field-wide">
                                        <span className="restaurant-detail-label">Event location</span>
                                        <span className="restaurant-detail-value">
                                            <EventLocationMapLink
                                                address={quote.deliveryAddress}
                                                emptyText="—"
                                            />
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="restaurant-detail-card restaurant-detail-card--line-items">
                                <div className="restaurant-detail-card-header">
                                    <h3 className="restaurant-detail-section-title">Line items</h3>
                                    <span className="order-details-card-meta">
                                        {lineItems.length} {lineItems.length === 1 ? 'item' : 'items'}
                                    </span>
                                </div>
                                <div className="order-details-table-wrap">
                                    <table className="order-details-rich-table">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Meal</th>
                                                <th>Qty</th>
                                                <th>Unit price</th>
                                                <th>Line total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lineItems.length > 0 ? (
                                                lineItems.map((li, index) => (
                                                    <tr
                                                        key={
                                                            li.cateringQuoteLineItemId ??
                                                            li.cateringItemId ??
                                                            index
                                                        }
                                                    >
                                                        <td>{displayValue(li.itemName)}</td>
                                                        <td>{displayValue(li.meal)}</td>
                                                        <td>{li.quantity ?? '—'}</td>
                                                        <td>{formatMoney(li.unitPrice)}</td>
                                                        <td>{formatMoney(li.price)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="order-details-table-empty">
                                                        No line items for this quote.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {lineItems.length > 0 ? (
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                                                        Items subtotal
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>
                                                        {formatMoney(lineItemsSubtotal)}
                                                    </td>
                                                </tr>
                                                {quoteTaxes.gst > 0 ? (
                                                    <tr>
                                                        <td colSpan={4} style={{ textAlign: 'right' }}>
                                                            GST
                                                        </td>
                                                        <td>{formatMoney(quoteTaxes.gst)}</td>
                                                    </tr>
                                                ) : null}
                                                {quoteTaxes.deliveryFee > 0 ? (
                                                    <tr>
                                                        <td colSpan={4} style={{ textAlign: 'right' }}>
                                                            Delivery fee
                                                        </td>
                                                        <td>{formatMoney(quoteTaxes.deliveryFee)}</td>
                                                    </tr>
                                                ) : null}
                                                {quoteTaxes.convienceFee > 0 ? (
                                                    <tr>
                                                        <td colSpan={4} style={{ textAlign: 'right' }}>
                                                            Convenience fee
                                                        </td>
                                                        <td>{formatMoney(quoteTaxes.convienceFee)}</td>
                                                    </tr>
                                                ) : null}
                                                {quoteTaxes.platformFee > 0 ? (
                                                    <tr>
                                                        <td colSpan={4} style={{ textAlign: 'right' }}>
                                                            Platform fee
                                                        </td>
                                                        <td>{formatMoney(quoteTaxes.platformFee)}</td>
                                                    </tr>
                                                ) : null}
                                                <tr>
                                                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                                                        Estimated total
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>
                                                        {formatMoney(
                                                            quote.estimatedTotal ??
                                                                quote.quotedTotal ??
                                                                lineItemsSubtotal +
                                                                    quoteTaxes.gst +
                                                                    quoteTaxes.deliveryFee +
                                                                    quoteTaxes.convienceFee +
                                                                    quoteTaxes.platformFee
                                                        )}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        ) : null}
                                    </table>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </RestaurantDashboardLayout>
    );
};

export default CateringQuoteDetail;
